import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Handmatige vernieuwknop als animerende pill.
 *
 * Ruststand: alleen het herlaad-icoontje (geen border, geen achtergrond).
 * Tijdens ophalen: het icoon draait.
 * Na succes: de pill vouwt open met "bijgewerkt om HH:MM" en klapt na 10s
 * vanzelf weer dicht naar alleen het icoon. Opnieuw klikken reset de timer.
 *
 * De zware refresh-logica zit in de pagina; dit component roept alleen
 * `onVervers` aan (een async functie) en regelt puur de visuele staat.
 *
 * Props:
 *   onVervers: () => Promise<void>   data opnieuw ophalen (licht, paginazaak)
 */
export default function VerversPill({ onVervers }) {
  const [bezig, setBezig] = useState(false);
  const [open, setOpen] = useState(false);
  const [tijd, setTijd] = useState(null);
  const dichtTimer = useRef(null);
  const gemount = useRef(true);

  useEffect(() => {
    gemount.current = true;
    return () => {
      gemount.current = false;
      if (dichtTimer.current) clearTimeout(dichtTimer.current);
    };
  }, []);

  const klik = useCallback(async () => {
    if (bezig) return;
    if (dichtTimer.current) { clearTimeout(dichtTimer.current); dichtTimer.current = null; }
    setBezig(true);
    try {
      await onVervers();
      if (!gemount.current) return;
      setTijd(new Date());
      setOpen(true);
      dichtTimer.current = setTimeout(() => {
        if (gemount.current) setOpen(false);
      }, 10000);
    } finally {
      if (gemount.current) setBezig(false);
    }
  }, [bezig, onVervers]);

  const label = tijd
    ? `bijgewerkt om ${tijd.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`
    : '';

  return (
    <button
      type="button"
      className={`ververs-pill${open ? ' open' : ''}`}
      onClick={klik}
      disabled={bezig}
      title="Gegevens opnieuw ophalen"
      aria-label="Gegevens opnieuw ophalen"
    >
      <svg
        width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        className={bezig ? 'ververs-icoon draait' : 'ververs-icoon'}
        style={{ flex: 'none' }}
      >
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
      </svg>
      <span className="ververs-label">{label}</span>
    </button>
  );
}
